import { MembershipProfile } from '@models/membership/MembershipProfile'
import { membershipProfileSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  fetchMembershipProfileById,
  fetchMembershipProfiles,
  updateMembershipProfile as updateMembershipProfileAPI,
  updateMembershipProfileCardNumber as updateMembershipProfileCardNumberAPI,
  updateTagsForMembershipProfile as updateTagsForMembershipProfileAPI,
} from '@services/membership/MembershipProfileService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<MembershipProfile> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'membershipProfile',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getMembershipProfilesSuccess,
  setQuery: setMembershipProfileQuery,
  reset: resetMembershipProfileState,
  resetQueryAndIds: resetMembershipProfileQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: MembershipProfile }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectMembershipProfiles = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedMembershipProfile = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectMembershipProfileById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectMembershipProfilesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )
/**
 * Actions
 */

export const setSelectedMembershipProfile =
  (membershipProfile: MembershipProfile | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<MembershipProfile>(membershipProfile)
      )
    )

export const getMembershipProfiles = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchMembershipProfiles(params),
    [membershipProfileSchema],
    slice.actions
  )
export const getMembershipProfileWithId = (id: number) =>
  SBAPI.FetchDispatched<MembershipProfile>(
    fetchMembershipProfileById(id),
    membershipProfileSchema,
    slice.actions
  )
export const updateMembershipProfile = (membershipProfile: MembershipProfile) =>
  SBAPI.UpdateDispatched<MembershipProfile>(
    updateMembershipProfileAPI(membershipProfile),
    membershipProfile,
    slice.actions
  )

export const updateMembershipProfileCardNumber = (
  membershipProfile: MembershipProfile
) =>
  SBAPI.UpdateDispatched<MembershipProfile>(
    updateMembershipProfileCardNumberAPI(membershipProfile),
    membershipProfile,
    slice.actions
  )

export const updateMembershipProfileTags = (
  membershipProfile: MembershipProfile
) =>
  SBAPI.UpdateDispatched<MembershipProfile>(
    updateTagsForMembershipProfileAPI(
      membershipProfile.id,
      membershipProfile.tags ?? []
    ),
    membershipProfile,
    slice.actions
  )
